import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productsApi } from '@/services/api';

export interface ProductFilterState {
  category_ids: number[];
  category_slugs: string[];
  product_types: string[];
  brands: string[];
  conditions: string[];
  locations: string[];
  latitude: number | undefined;
  longitude: number | undefined;
  radius_km: number;
  min_price: number | undefined;
  max_price: number | undefined;
  search_query: string;
  skip: number;
  limit: number;
}

// Load filters from localStorage
const loadFiltersFromStorage = (): Partial<ProductFilterState> => {
  try {
    const saved = localStorage.getItem('product_filters');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        category_ids: parsed.category_ids || [],
        product_types: parsed.product_types || [],
        brands: parsed.brands || [],
        conditions: parsed.conditions || [],
        locations: parsed.locations || [],
        latitude: parsed.latitude,
        longitude: parsed.longitude,
        radius_km: parsed.radius_km || 50,
        min_price: parsed.min_price,
        max_price: parsed.max_price,
        search_query: parsed.search_query || '',
      };
    }
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
  }
  return {};
};

// Save filters to localStorage
const saveFiltersToStorage = (filters: Partial<ProductFilterState>) => {
  try {
    const toSave = {
      category_ids: filters.category_ids || [],
      product_types: filters.product_types || [],
      brands: filters.brands || [],
      conditions: filters.conditions || [],
      locations: filters.locations || [],
      latitude: filters.latitude,
      longitude: filters.longitude,
      radius_km: filters.radius_km || 50,
      min_price: filters.min_price,
      max_price: filters.max_price,
      search_query: filters.search_query || '',
    };
    localStorage.setItem('product_filters', JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};

export interface FilteredProductsResponse {
  products: any[];
  total: number;
  page: number;
  pages: number;
}

const savedFilters = loadFiltersFromStorage();

const initialState = {
  category_ids: savedFilters.category_ids || [],
  category_slugs: [] as string[],
  product_types: savedFilters.product_types || [],
  brands: savedFilters.brands || [],
  conditions: savedFilters.conditions || [],
  locations: savedFilters.locations || [],
  latitude: savedFilters.latitude,
  longitude: savedFilters.longitude,
  radius_km: savedFilters.radius_km || 50,
  min_price: savedFilters.min_price,
  max_price: savedFilters.max_price,
  search_query: savedFilters.search_query || '',
  skip: 0,
  limit: 20,
  isLoading: false,
  error: null as string | null,
  filteredProducts: [] as any[],
  total: 0,
  page: 1,
  pages: 0,
};

export const filterProducts = createAsyncThunk(
  'productFilters/filterProducts',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { productFilters: ProductFilterState };
    const filters = state.productFilters;

    console.log('filterProducts thunk called with filters:', {
      category_ids: filters.category_ids,
      category_slugs: filters.category_slugs,
      product_types: filters.product_types,
      brands: filters.brands,
      conditions: filters.conditions,
      locations: filters.locations,
      latitude: filters.latitude,
      longitude: filters.longitude,
      radius_km: filters.radius_km,
      min_price: filters.min_price,
      max_price: filters.max_price,
      search_query: filters.search_query,
      skip: filters.skip || 0,
      limit: filters.limit || 20,
    });

    try {
      const response = await productsApi.filterProducts({
        category_ids: filters.category_ids,
        category_slugs: filters.category_slugs,
        product_types: filters.product_types,
        brands: filters.brands,
        conditions: filters.conditions,
        locations: filters.locations,
        latitude: filters.latitude,
        longitude: filters.longitude,
        radius_km: filters.radius_km,
        min_price: filters.min_price,
        max_price: filters.max_price,
        search_query: filters.search_query,
        skip: filters.skip || 0,
        limit: filters.limit || 20,
      });
      
      // Backend now returns pagination metadata
      const responseData = response.data;
      const products = responseData.items || responseData; // Support both formats for backward compatibility
      const total = responseData.total || products.length;
      const page = responseData.page || Math.floor((filters.skip || 0) / (filters.limit || 20)) + 1;
      const pages = responseData.pages || Math.ceil(total / (filters.limit || 20));
      
      console.log('filterProducts API response:', {
        productsCount: products.length,
        total,
        page,
        pages,
        skip: filters.skip || 0,
        limit: filters.limit || 20
      });
      
      return {
        products: Array.isArray(products) ? products : [],
        total,
        page,
        pages,
      };
    } catch (error: any) {
      console.error('filterProducts API error:', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to filter products');
    }
  }
);

const productFiltersSlice = createSlice({
  name: 'productFilters',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const { category_ids, category_slugs, product_types, brands, conditions, locations, min_price, max_price, search_query } = action.payload;

      if (category_ids !== undefined) state.category_ids = category_ids;
      if (category_slugs !== undefined) state.category_slugs = category_slugs;
      if (product_types !== undefined) state.product_types = product_types;
      if (brands !== undefined) state.brands = brands;
      if (conditions !== undefined) state.conditions = conditions;
      if (locations !== undefined) state.locations = locations;
      if (min_price !== undefined) state.min_price = min_price;
      if (max_price !== undefined) state.max_price = max_price;
      if (search_query !== undefined) state.search_query = search_query;

      // Reset pagination when filters change
      state.skip = 0;
      state.page = 1;

      // Save to localStorage
      saveFiltersToStorage(state);
    },
    
    toggleCategory: (state, action) => {
      const categoryId = action.payload;
      const index = state.category_ids.indexOf(categoryId);
      if (index > -1) {
        state.category_ids.splice(index, 1);
      } else {
        state.category_ids.push(categoryId);
      }
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    toggleProductType: (state, action) => {
      const productType = action.payload;
      const index = state.product_types.indexOf(productType);
      if (index > -1) {
        state.product_types.splice(index, 1);
      } else {
        state.product_types.push(productType);
      }
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    toggleBrand: (state, action) => {
      const brand = action.payload;
      const index = state.brands.indexOf(brand);
      if (index > -1) {
        state.brands.splice(index, 1);
      } else {
        state.brands.push(brand);
      }
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    toggleCondition: (state, action) => {
      const condition = action.payload;
      const index = state.conditions.indexOf(condition);
      if (index > -1) {
        state.conditions.splice(index, 1);
      } else {
        state.conditions.push(condition);
      }
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    toggleLocation: (state, action) => {
      const location = action.payload;
      const index = state.locations.indexOf(location);
      if (index > -1) {
        state.locations.splice(index, 1);
      } else {
        state.locations.push(location);
      }
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    setPriceRange: (state, action) => {
      const { min_price, max_price } = action.payload;
      state.min_price = min_price;
      state.max_price = max_price;
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    setSearchQuery: (state, action) => {
      state.search_query = action.payload;
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    clearAllFilters: (state) => {
      state.category_ids = [];
      state.category_slugs = [];
      state.product_types = [];
      state.brands = [];
      state.conditions = [];
      state.locations = [];
      state.min_price = undefined;
      state.max_price = undefined;
      state.search_query = '';
      state.skip = 0;
      state.page = 1;
      saveFiltersToStorage(state);
    },
    
    setPage: (state, action) => {
      state.page = action.payload;
      state.skip = (action.payload - 1) * state.limit;
    },
    
    setSkip: (state, action) => {
      state.skip = action.payload;
      state.page = Math.floor(action.payload / state.limit) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(filterProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(filterProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredProducts = action.payload.products;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pages = action.payload.pages;
        state.error = null;
      })
      .addCase(filterProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.filteredProducts = [];
        state.total = 0;
        state.page = 1;
        state.pages = 0;
      });
  },
});

export const {
  setFilters,
  toggleCategory,
  toggleProductType,
  toggleBrand,
  toggleCondition,
  toggleLocation,
  setPriceRange,
  setSearchQuery,
  clearAllFilters,
  setPage,
  setSkip,
} = productFiltersSlice.actions;

export default productFiltersSlice.reducer;
